import type { EditorAPI } from '../../types';
import { useEffect, useRef, useState } from 'react';
import { getSelectedNodeLocalId } from '../../utils/getSelectedNodeLocalId';
import type { ForgeUIExtensionType } from '@atlassian/forge-ui-types';
import { dequal } from 'dequal/lite';

interface UseShouldRenderRuntimeProps {
	editorAPI?: EditorAPI;
	extensionProperties?: ForgeUIExtensionType['properties'];
	localId: string;
	macroConfig?: Record<string, unknown>;
}

const RUNTIME_UNMOUNT_TIMEOUT = 1000;

export const useShouldRenderRuntime = ({
	editorAPI,
	extensionProperties,
	macroConfig,
	localId,
}: UseShouldRenderRuntimeProps) => {
	const isEmitReadyEventFromManifest = extensionProperties?.emitsReadyEvent;

	const previousCustomConfigRef = useRef<Record<string, unknown> | null | undefined>(macroConfig);

	const [shouldRenderRuntime, setShouldRenderRuntime] = useState(false);

	const hasCustomConfig = !!extensionProperties?.config?.resource;

	// Handles static macros with classic macro config
	useEffect(() => {
		if (hasCustomConfig || !editorAPI?.extension?.sharedState) {
			return;
		}

		const unsubscribe = editorAPI.extension.sharedState.onChange(
			({ nextSharedState, prevSharedState }) => {
				const prevIsOpen = prevSharedState?.showContextPanel;
				const currIsOpen = nextSharedState?.showContextPanel;
				// This listener is currently set to all macros with a classic config
				// Check if the selected node is the same as the static macro
				const selectedNodeLocalId = getSelectedNodeLocalId(editorAPI) || null;
				const isSelected = selectedNodeLocalId === localId;

				// Context panel was just opened
				// Runtime must be mounted
				if (isSelected) {
					if (!prevIsOpen && currIsOpen) {
						setShouldRenderRuntime(true);
					}
				}
				// If the context panel is closed, the runtime is unmounted.
				// Any changes to the config will be applied via the useEffect below
				// Handles context panel opening and closing without any changes to the config being made
				if (prevIsOpen && !currIsOpen) {
					setShouldRenderRuntime(false);
				}
			},
		);

		return () => {
			unsubscribe();
		};
	}, [editorAPI, hasCustomConfig, localId]);

	useEffect(() => {
		// Watch for changes to the config
		// Mounts the runtime briefly to allow the config to be applied to the main macro before unmounting again
		if (!dequal(previousCustomConfigRef.current, macroConfig)) {
			previousCustomConfigRef.current = macroConfig;
			setShouldRenderRuntime(true);

			// App has set emitsReadyEvent to true in the manifest - we should not be unmounting the runtime ourselves
			// Instead the bridge method will call unmountRuntime when the snapshot is ready
			if (isEmitReadyEventFromManifest) {
				return;
			}

			// Remove the runtime after 1 second to allow after it finishes updating the main macro with the updated config
			setTimeout(() => {
				setShouldRenderRuntime(false);
			}, RUNTIME_UNMOUNT_TIMEOUT);
		}
	}, [macroConfig, localId, isEmitReadyEventFromManifest]);

	return {
		// always render runtime for dynamic macros
		shouldRenderRuntime,
		unmountRuntime: () => setShouldRenderRuntime(false),
	};
};
